import {
    Component,
    OnInit,
    Inject,
    ViewChild,
    OnDestroy,
    AfterViewInit,
    Input
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as fromGroupsR from '../store/reducer';
import * as fromErrors from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info/store/user.reducer';
import { Observable, combineLatest, ReplaySubject, of } from 'rxjs';
import {
    IGroup,
    IGroupMember,
    IGroupElement,
    IGroupType,
    IGroupElementType
} from '../IGroups';
import { filter, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { CustomerAdminService } from '../../customer-admin/customer-admin.service';
import { CompanyInfo } from '../../customer-admin/customer-admin.model';
import * as fromAuth from '../../auth/store/auth.reducer';
import {
    LoadGroupMembers,
    LoadGroupTypes,
    LoadGroups,
    LoadGroupElements,
    LoadGroupElementTypes,
    UpdateGroupElements,
    UpdateGroupMembers,
    DeleteGroup,
    Reset,
    SetGroupElements,
    SetGroupMembers,
    SetGroupElementTypes,
    SetGroups
} from '../store/actions';
import { GroupsPresentersComponent } from '../presenters/groups-presenters.component';
import { UserRoleEnum } from '../../user-info';
import { IShowErrors } from '../../shared/errors-sidenav';
import * as fromShared from '../../shared';
import * as fromGroupsA from '../store/actions';

@Component({
    selector: 'ef-groups-feature',
    templateUrl: './groups-feature.component.html',
    styleUrls: ['./groups-feature.component.scss'],
    standalone: false
})
export class GroupsFeatureComponent
    implements OnInit, OnDestroy, AfterViewInit {
    private active = true;
    public selectedGroup: IGroup;
    public groupIDs: Array<number> = [];
    process$: Observable<fromShared.Process>;
    public groups$: Observable<Array<IGroup>>;
    public groups: Array<IGroup> = [];
    public groupMembers$: Observable<Array<IGroupMember>>;
    public groupMembers: Array<IGroupMember> = [];
    public groupMembersORIG: Array<IGroupMember> = [];
    public groupElements$: Observable<Array<IGroupElement>>;
    public groupElements: Array<IGroupElement> = [];
    public groupElementsORIG: Array<IGroupElement> = [];
    public groupTypes$: Observable<Array<IGroupType>>;
    public groupTypes: Array<IGroupType> = [];
    public groupElementTypes$: Observable<Array<IGroupElementType>>;
    public groupElementTypes: Array<IGroupElementType> = [];
    selectedError$: Observable<IShowErrors>;
    /** Licensed administrator limit from CompanyInfo.AdminCount (minimum 1). */
    public adminCount = 1;

    @ViewChild(GroupsPresentersComponent, { static: true })
    groupsPresComp: GroupsPresentersComponent;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    constructor(
        private groupsStore: Store<fromGroupsR.State>,
        @Inject(LoggingService) private log: LoggingService,
        private store: Store<fromUser.State>,
        private errorStore: Store<fromShared.Errors.State>,
        private customerAdminService: CustomerAdminService
    ) {}

    ngOnInit() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.groups$ = this.groupsStore.pipe(select(fromGroupsR.getGroups));

        this.groupMembers$ = this.groupsStore.pipe(
            select(fromGroupsR.getGroupMembers)
        );

        this.groupElements$ = this.groupsStore.pipe(
            select(fromGroupsR.getGroupElements)
        );

        this.groupElementTypes$ = this.groupsStore.pipe(
            select(fromGroupsR.getGroupElementTypes)
        );

        this.groupTypes$ = this.groupsStore.pipe(
            select(fromGroupsR.getGroupTypes)
        );

        this.selectedError$ = this.errorStore.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(x =>
                this.log.verbose({
                    type: 'ManageUsersFeatureComponent:selectedError$',
                    x
                })
            )
        );

        this.loadAdminCount();
    }

    private loadAdminCount(): void {
        this.store
            .pipe(
                select(fromAuth.getAuth),
                filter(auth => !!auth?.CustomerID),
                take(1),
                switchMap(auth =>
                    this.customerAdminService.get(auth.CustomerID, false).pipe(
                        map(resp => {
                            const items = JSON.parse(resp) as CompanyInfo[];
                            return items?.length > 0
                                ? items[0].AdminCount ?? 1
                                : 1;
                        })
                    )
                ),
                takeWhile(() => this.active)
            )
            .subscribe({
                next: adminCount => {
                    this.adminCount = adminCount ?? 1;
                    this.log.verbose({
                        type: 'GroupsFeatureComponent:loadAdminCount',
                        adminCount
                    });
                },
                error: err =>
                    this.log.verbose({
                        type: 'GroupsFeatureComponent:loadAdminCount:error',
                        err
                    })
            });
    }

    onLoadMembers($event) {
        this.groupsStore.dispatch(new LoadGroupMembers($event));
    }

    ngOnDestroy() {
        this.active = false;
    }

    ngAfterViewInit() {
        this.groupsStore.dispatch(new LoadGroupTypes());

        this.process$.pipe(takeWhile(() => this.active)).subscribe(process => {
            if (process.ProcessID == 141) {
                this.groupsStore.dispatch(
                    new LoadGroups({ Categoryid: 'SYS000000000000000000000000000000000' })
                );
            }
            this.setProcessRole(this.getProcessRole(process.ProcessID));
        });

        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });

        this.groups$.pipe(takeWhile(_ => this.active)).subscribe(_groups => {
            this.groups = _groups;
        });

        this.groupMembers$
            .pipe(
                takeWhile(_ => this.active),
                tap(x =>
                    this.log.debug({
                        type: 'GroupsFeatureComponent:groupMembers$',
                        x
                    })
                )
            )
            .subscribe(members => {
                this.groupMembers = members;
                this.groupMembersORIG = members;
            });

        this.groupElements$
            .pipe(takeWhile(_ => this.active))
            .subscribe(elements => {
                this.groupElements = elements;
                this.groupElementsORIG = elements;
            });

        this.groupTypes$.pipe(takeWhile(_ => this.active)).subscribe(types => {
            this.groupTypes = types;
        });

        this.groupElementTypes$
            .pipe(takeWhile(_ => this.active))
            .subscribe(elementtypes => {
                this.groupElementTypes = elementtypes;
            });

        this.groupsPresComp.selectGroupType
            .pipe(takeWhile(_ => this.active))
            .subscribe(grouptype => {
                const selectedType: IGroupType = grouptype;
                if (selectedType != null) {
                    this.groupsStore.dispatch(
                        new LoadGroups({ Categoryid: selectedType.Categoryid })
                    );
                }
                this.log.debug({
                    type: 'GroupsFeatureComponent:selectGroupType',
                    selectedType
                });
            });

        this.groupsPresComp.selectGroup
            .pipe(takeWhile(_ => this.active))
            .subscribe(group => {
                this.selectedGroup = group;
                if (this.selectedGroup != null) {
                    this.groupsStore.dispatch(
                        new LoadGroupMembers({
                            GroupID: this.selectedGroup.GroupID
                        })
                    );
                    this.groupsStore.dispatch(
                        new LoadGroupElementTypes({
                            GroupID: this.selectedGroup.GroupID
                        })
                    );
                }
                // this.log.debug({ type: 'GroupsFeatureComponent:selectGroupType', this.selectedGroup });
            });

        this.groupsPresComp.selectElement
            .pipe(takeWhile(_ => this.active))
            .subscribe(element => {
                const selectedElement: IGroupElementType = element;
                if (selectedElement != null) {
                    this.groupsStore.dispatch(
                        new LoadGroupElements({
                            GroupID: selectedElement.GroupID,
                            GroupElementTypeID:
                                selectedElement.GroupElementTypeID,
                            ListID: selectedElement.ListID
                        })
                    );
                }
                this.log.debug({
                    type: 'GroupsFeatureComponent:selectGroupType',
                    selectedElement
                });
            });

        this.groupsPresComp.toggleMode
            .pipe(takeWhile(_ => this.active))
            .subscribe(x => {
                if (x === false) {
                    this.groupMembers = this.groupMembers.filter(
                        y => y.IsGroupMember === true
                    );
                    this.groupElements = this.groupElements.filter(
                        z => z.IsGroupElement === true
                    );
                } else {
                    this.groupMembers = this.groupMembersORIG;
                    this.groupElements = this.groupElementsORIG;
                }
                this.log.debug({
                    type: 'GroupsFeatureComponent:toggleMode',
                    x
                });
            });

        this.groupsPresComp.toggleModeRoles
            .pipe(takeWhile(_ => this.active))
            .subscribe(x => {
                if (x === true) {
                    this.groupMembers = this.groupMembers.filter(
                        y => y.IsGroupMember === true
                    );
                    this.groupElements = this.groupElements.filter(
                        z => z.IsGroupElement === true
                    );
                } else {
                    this.groupMembers = this.groupMembersORIG;
                    this.groupElements = this.groupElementsORIG;
                }
                this.log.debug({
                    type: 'GroupsFeatureComponent:toggleModeRoles',
                    x
                });
            });

        this.groupsPresComp.deleteGroup
            .pipe(takeWhile(_ => this.active))
            .subscribe(x => {
                this.groupIDs.push(x.GroupID);
                this.groupsStore.dispatch(new DeleteGroup(this.groupIDs));
                this.groups = [];
                this.groupsStore.dispatch(new SetGroups([]));
                this.groupsStore.dispatch(new SetGroupMembers([]));
                this.groupsStore.dispatch(new SetGroupElements([]));
                this.groupsStore.dispatch(new SetGroupElementTypes([]));
                this.groupMembers = [];
                this.groupMembersORIG = [];
                this.groupElements = [];
                this.groupElementsORIG = [];
                this.groupElementTypes = [];
                this.selectedGroup = null;

                this.log.debug({
                    type: 'GroupsFeatureComponent:deleteGroup',
                    x
                });
                this.groupIDs = [];
            });
    }

    public onMemberRowSelectUpdate($event) {
        this.groupsStore.dispatch(new UpdateGroupMembers($event));
    }

    public onElementRowSelectUpdate($event) {
        this.groupsStore.dispatch(new UpdateGroupElements($event));
    }

    public onResetData() {
        this.groupsStore.dispatch(new Reset());
    }

    getProcessRole(processID: number): UserRoleEnum {
        let roleId;
        switch (processID) {
            case 139:
                roleId = UserRoleEnum.Administrator;
                break;
            case 73:
                roleId = UserRoleEnum.Supervisor;
                break;
            case 140:
                roleId = UserRoleEnum.TaskManager;
                break;
            case 141:
                roleId = UserRoleEnum.ReportAdmin;
                break;
            case 142:
                roleId = UserRoleEnum.Subcontractor;
                break;
            default:
                // roleId = user.DefaultRoleGroupID;
                break;
        }
        return roleId;
    }

    setProcessRole(roleId) {
        if (roleId === 1) {
            this.groupsStore.dispatch(new LoadGroupMembers({ GroupID: 1 }));
            this.groupsStore.dispatch(
                new LoadGroupElementTypes({ GroupID: 1 })
            );
        } else if (roleId === 12024) {
            this.groupsStore.dispatch(new LoadGroupMembers({ GroupID: 12024 }));
            this.groupsStore.dispatch(
                new LoadGroupElementTypes({ GroupID: 12024 })
            );
        } else if (roleId === 12025) {
            this.groupsStore.dispatch(new LoadGroupMembers({ GroupID: 12025 }));
            this.groupsStore.dispatch(
                new LoadGroupElementTypes({ GroupID: 12025 })
            );
        } else if (roleId === 12029) {
            this.groupsStore.dispatch(new LoadGroupMembers({ GroupID: 12029 }));
            this.groupsStore.dispatch(
                new LoadGroupElementTypes({ GroupID: 12029 })
            );
        } else if (roleId === 12030) {
            this.groupsStore.dispatch(new LoadGroupMembers({ GroupID: 12030 }));
            this.groupsStore.dispatch(
                new LoadGroupElementTypes({ GroupID: 12030 })
            );
        } else {
            // Do nothing
        }
    }

    OnShowError(event) {
        this.ShowError.next(event);
    }
}
